import { useCallback, useEffect, useMemo, useState } from "react";

import "./CfdFooterSection.css";
import {
  getCfdPropertyBuyerPremium,
  getCfdPropertyDetailsForOffer,
  getCfdPropertyDisclaimer,
  getCfdPropertyOfferHistory,
  getCfdPropertyOfferSummaryHistory,
  submitCfdPropertyOffer,
} from "@/api/cfd";
import { debounce } from "lodash";
import DisclaimerModal from "./modals/DisclaimerModal";
import OfferSummaryModal from "./modals/OfferSummaryModal";
import OfferHistoryModal from "./modals/OfferHistoryModal";
import MakeOfferModal from "./modals/MakeOfferModal";
import CfdFavoriteButton from "../CfdFavoriteButton";
import { trackApiCall, trackButtonClick } from "@/utils/analytics";

const CfdFooterSection = ({
  item,
  onRefresh,
  onNavigatePDP,
  activeTab,
  isModalOpen,
  isPropertySaved,
  setIsPropertySaved,
  setShowModal,
}) => {
  const isSavedTab = activeTab === "saved";
  const historyTab = activeTab === "history";
  const currentBid = isSavedTab ? item.currentCfdHighestBid : item.currentBid;
  const propertyId = isSavedTab
    ? item?.propertyDetails?.propertyId
    : item?.propertyId;

  const [historyModalState, setHistoryModalState] = useState({
    isLoading: false,
    offerHistoryData: null,
    error: null,
  });

  const [disclaimerModalState, setDisclaimerModalState] = useState({
    isLoading: false,
    disclaimerData: null,
    error: null,
  });

  const [makeOfferModalState, setMakeOfferModalState] = useState({
    isLoading: false,
    propertyData: null,
    error: null,
  });

  const [offerSummaryModalState, setOfferSummaryModalState] = useState({
    isLoading: false,
    propertyData: null,
    error: null,
  });

  const [buyersPremiumData, setBuyersPremiumData] = useState({
    bidAmount: 0,
    bidAmountFormatted: 0,
    buyerPremiumFormatted: 0,
    totalDueFormatted: 0,
    buyerPremium: 0,
    premiumPaidBy: "",
    technologyFee: null,
    earnestMoney: null,
    earnestMoneyMessage: null,
  });

  const [showDisclaimerModal, setShowDisclaimerModal] = useState(false);
  const [showOfferHistory, setShowOfferHistory] = useState(false);
  const [showPlaceOffer, setShowPlaceOffer] = useState(false);
  const [showOfferSummaryModal, setShowOfferSummaryModal] = useState(false);
  const [offerAmount, setOfferAmount] = useState(0);
  const [minBidAmount, setMinBidAmount] = useState(0);
  const [currentLocalAmnt, setCurrentLocalAmnt] = useState(0);
  const [offerAmountError, setOfferAmountError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [offerSubmissionState, setOfferSubmissionState] = useState({
    data: null,
    status: "", // 'SUCCESS', 'ERROR', 'PENDING', ''
    errorCode: "",
    errorMessage: "",
    errorTrace: "",
    loading: false,
    successMessage: "",
  });

  // Created a separate function for setting buyer premium data
  const updateBuyerPremiumData = useCallback(
    (data, newOfferAmnt) => {
      setBuyersPremiumData({
        bidAmount: newOfferAmnt ? newOfferAmnt : data.bidAmount || 0,
        bidAmountFormatted: data.bidAmountFormatted || "$0",
        buyerPremiumFormatted: data.buyerPremiumFormatted || "$0",
        totalDueFormatted: data.totalDueFormatted || "$0",
        buyerPremium: data.buyerPremium || 0,
        premiumPaidBy: data.premiumPaidBy || "",
        technologyFee: data.technologyFeeFormatted,
        earnestMoney: data.earnestMoneyFormatted,
        earnestMoneyMessage: data.earnestMoneyMessage,
      });
    },
    [buyersPremiumData]
  );

  // Created a memoized fetch function
  const fetchBuyerPremium = useCallback(
    async (amount) => {
      try {
        const response = await getCfdPropertyBuyerPremium(propertyId, amount);
        return response;
      } catch (error) {
        throw new Error("Failed to fetch buyer premium data");
      }
    },
    [propertyId, isLoading]
  );

  const debouncedFetchOfferDetails = useMemo(
    () =>
      debounce(async (amount) => {
        setOfferAmountError("");
        setIsLoading(true);
        setOfferSubmissionState((prevState) => ({
          ...prevState,
          loading: true,
        }));

        try {
          const buyerPremiumData = await fetchBuyerPremium(amount);

          if (!buyerPremiumData) {
            throw new Error("No data received from API");
          }

          setOfferAmount(buyerPremiumData.bidAmount);
          updateBuyerPremiumData(buyerPremiumData);
        } catch (error) {
          console.error("Error fetching offer details:", error);
          setOfferAmountError("Failed to update offer details");
        } finally {
          setIsLoading(false);
          setOfferSubmissionState((prevState) => ({
            ...prevState,
            loading: false,
          }));
        }
      }, 500),
    [offerSubmissionState]
  );

  const retrieveOfferHistory = async () => {
    trackButtonClick({
      eventName: "cfd_view_offer_history",
      eventCategory: "CFD",
      eventSource: "on view offer history text button click",
      eventLabel: `Property Id: ${propertyId}`,
    });

    try {
      setHistoryModalState((prevState) => ({
        ...prevState,
        isLoading: true,
        error: null,
      }));

      const result = await getCfdPropertyOfferHistory(propertyId);

      if (result.status === "SUCCESS") {
        setHistoryModalState((prevState) => ({
          ...prevState,
          offerHistoryData: result.responseList,
        }));
      } else {
        setHistoryModalState((prevState) => ({
          ...prevState,
          offerHistoryData: null,
        }));
      }
    } catch (error) {
      console.error("🚀 ~ retrieveOfferHistory ~ error:", error);
      setHistoryModalState((prevState) => ({
        ...prevState,
        error: "Something went wrong. Please try again later.",
        offerHistoryData: null,
      }));
    } finally {
      setHistoryModalState((prevState) => ({
        ...prevState,
        isLoading: false,
      }));
    }
  };

  const getPropertyDisclaimer = async () => {
    trackButtonClick({
      eventName: "cfd_view_disclaimers_clicked",
      eventCategory: "CFD",
      eventSource: "on view disclaimer text button click",
      eventLabel: `Property Id: ${propertyId}`,
    });

    try {
      setDisclaimerModalState((prevState) => ({
        ...prevState,
        isLoading: true,
        error: null,
      }));

      const result = await getCfdPropertyDisclaimer(propertyId);

      if (result.status === "SUCCESS") {
        setDisclaimerModalState((prevState) => ({
          ...prevState,
          disclaimerData: result.data,
        }));
      } else {
        trackApiCall({
          eventName: "cfd_error_open_disclaimers",
          eventCategory: "Error",
          eventSource: "on disclaimer fetch error",
        });

        setDisclaimerModalState((prevState) => ({
          ...prevState,
          disclaimerData: null,
        }));
      }
    } catch (error) {
      console.error("🚀 ~ retrieveOfferHistory ~ error:", error);
      setDisclaimerModalState((prevState) => ({
        ...prevState,
        error: "Something went wrong. Please try again later.",
        disclaimerData: null,
      }));
    } finally {
      setDisclaimerModalState((prevState) => ({
        ...prevState,
        isLoading: false,
      }));
    }
  };

  const fetchPropertyDetailsForMakeAnOffer = async (placeNewOffer) => {
    trackButtonClick({
      eventName: "cfd_open_offer_modal",
      eventCategory: "CFD",
      eventSource: "on place/make offer button click",
      eventLabel: "Property Id: " + propertyId,
      eventParams: { offer_mode: placeNewOffer ? "place_new_offer" : "make_offer" },
    });

    try {
      setMakeOfferModalState((prevState) => ({
        ...prevState,
        isLoading: true,
        error: null,
      }));

      const result = await getCfdPropertyDetailsForOffer(propertyId);

      if (result.status === "SUCCESS") {
        setMakeOfferModalState((prevState) => ({
          ...prevState,
          propertyData: result.data,
        }));

        const bidIncrementalAmnt =
          result.data?.listingPriceDetail?.biddingIncrementalAmount;
        const currentHighestBid =
          result.data?.listingPriceDetail?.currentHighestBid;
        const cfdMinOfferAmount =
          result.data?.propertyDetails?.cfdMinOfferAmount;
        let offerAmount;
        if (currentHighestBid) {
          offerAmount =
            parseInt(currentHighestBid) + parseInt(bidIncrementalAmnt);
        } else {
          offerAmount = cfdMinOfferAmount;
        }
        const buyerPremiumData = await fetchBuyerPremium(offerAmount);

        if (!buyerPremiumData) {
          throw new Error("No data received from API");
        }

        setMinBidAmount(buyerPremiumData.bidAmount);
        setOfferAmount(buyerPremiumData.bidAmount);
        setCurrentLocalAmnt(buyerPremiumData.bidAmount);
        updateBuyerPremiumData(buyerPremiumData);
      } else {
        setMakeOfferModalState((prevState) => ({
          ...prevState,
          propertyData: null,
        }));
        setOfferAmount(0);
        setMinBidAmount(0);
        setCurrentLocalAmnt(0);
      }
    } catch (error) {
      console.error("🚀 ~ retrieveOfferHistory ~ error:", error);
      setMakeOfferModalState((prevState) => ({
        ...prevState,
        error: "Something went wrong. Please try again later.",
        propertyData: null,
      }));
      setOfferAmount(0);
      setMinBidAmount(0);
      setCurrentLocalAmnt(0);
    } finally {
      setMakeOfferModalState((prevState) => ({
        ...prevState,
        isLoading: false,
      }));
    }
  };

  const retrieveOfferSummary = async (propertyID, offerID) => {
    trackButtonClick({
      eventName: "cfd_clicked_offer_summary",
      eventCategory: "CFD",
      eventSource: "on view offer summary text button click",
      eventLabel: `Property Id: ${propertyID}`,
    });

    try {
      setOfferSummaryModalState((prevState) => ({
        ...prevState,
        isLoading: true,
        error: null,
      }));

      const result = await getCfdPropertyOfferSummaryHistory(
        propertyID,
        offerID
      );

      if (result.status === "SUCCESS") {
        setOfferSummaryModalState((prevState) => ({
          ...prevState,
          propertyData: result,
        }));
      } else {
        setOfferSummaryModalState((prevState) => ({
          ...prevState,
          propertyData: null,
        }));
      }
    } catch (error) {
      console.error("🚀 ~ retrieveOfferHistory ~ error:", error);
      setOfferSummaryModalState((prevState) => ({
        ...prevState,
        error: "Something went wrong. Please try again later.",
        propertyData: null,
      }));
    } finally {
      setOfferSummaryModalState((prevState) => ({
        ...prevState,
        isLoading: false,
      }));
    }
  };

  const handleInputChange = (amnt) => {
    const value = parseFloat(amnt);

    setCurrentLocalAmnt(value);
    const incrementBy =
      makeOfferModalState.propertyData?.listingPriceDetail
        ?.biddingIncrementalAmount || 1;

    if (value < minBidAmount) {
      setOfferAmountError(`Oops! Minimum bid is $${minBidAmount}`);
      setOfferAmount(value);
      return;
    }

    if (incrementBy > 0) {
      const remainder = (value - minBidAmount) % incrementBy;
      if (remainder !== 0) {
        setOfferAmountError(`Please use $${incrementBy} increments`);
        setOfferAmount(value);
        return;
      }
    }

    trackButtonClick({
      eventName: "cfd_bid_validation",
      eventCategory: "CFD",
      eventSource: "on input change",
    });

    setOfferAmount(value);
    setOfferAmountError("");
    debouncedFetchOfferDetails(value);
  };

  const handleIncrement = () => {
    trackButtonClick({
      eventName: "cfd_offer_amt_inc_clicked",
      eventCategory: "CFD",
      eventSource: "on plus icon click",
    });

    let newAmount;
    const incrementBy =
      makeOfferModalState.propertyData?.listingPriceDetail
        ?.biddingIncrementalAmount || 1;

    if (currentLocalAmnt < minBidAmount) {
      // If below minimum, set to minimum
      newAmount = minBidAmount;
    } else {
      // Round up to nearest hundred
      newAmount = Math.ceil(offerAmount / incrementBy) * incrementBy;
      // newAmount = offerAmount + incrementBy;
      if (newAmount === offerAmount) {
        newAmount += incrementBy;
      }
    }

    setCurrentLocalAmnt(newAmount);
    setOfferAmount(newAmount);
    debouncedFetchOfferDetails(newAmount);
  };

  const handleDecrement = () => {
    trackButtonClick({
      eventName: "cfd_offer_amt_dec_clicked",
      eventCategory: "CFD",
      eventSource: "on minus icon click",
    });

    const incrementBy =
      makeOfferModalState.propertyData?.listingPriceDetail
        ?.biddingIncrementalAmount || 1;

    // Round down to nearest hundred
    const roundedDown = Math.floor(offerAmount / incrementBy) * incrementBy;
    const newAmount =
      roundedDown === offerAmount ? offerAmount - incrementBy : roundedDown;

    // Ensure we don't go below minimum bid amount
    if (newAmount >= minBidAmount) {
      setCurrentLocalAmnt(newAmount);
      setOfferAmount(newAmount);
      debouncedFetchOfferDetails(newAmount);
    }
  };

  const handleSubmitOffer = async () => {
    try {
      if (!offerAmount || offerAmountError) return;

      // Set loading state
      setOfferSubmissionState({
        data: null,
        status: "PENDING",
        errorCode: "",
        errorMessage: "",
        errorTrace: "",
        loading: true,
        successMessage: "",
      });

      trackButtonClick({
        eventName: "cfd_make_offer_submit_clicked",
        eventCategory: "CFD",
        eventSource: "on submit button click",
      });

      const offerData = {
        buyerPremium: buyersPremiumData.buyerPremium,
        bidAmount: buyersPremiumData.bidAmount,
        propertyId: propertyId,
      };

      const result = await submitCfdPropertyOffer(
        offerData.buyerPremium,
        offerData.bidAmount,
        offerData.propertyId
      );

      // Handle the response based on status
      switch (result.status) {
        case "SUCCESS":
          if (result.data?.bidPlacedSuccessfully) {
            setOfferSubmissionState({
              data: result.data,
              status: "SUCCESS",
              errorCode: null,
              errorMessage: null,
              errorTrace: null,
              loading: false,
              successMessage:
                "Thank you for submitting your offer. You will be receiving a confirmation email with offer details, and our team will reach out to you shortly.",
            });
          } else {
            // Even though status is SUCCESS, bid wasn't placed successfully
            setOfferSubmissionState({
              data: result.data,
              status: "ERROR",
              errorCode: "BID_FAILED",
              errorMessage:
                "Your offer could not be processed. Please try again.",
              errorTrace: null,
              loading: false,
              successMessage: null,
            });
          }
          break;

        case "FAILURE":
          setOfferSubmissionState({
            data: null,
            status: "ERROR",
            errorCode: result.errorCode || "UNKNOWN_ERROR",
            errorMessage:
              result.errorMessage ||
              "Unable to process your offer at this time. Please try again later.",
            errorTrace: result.errorTrace || null,
            loading: false,
            successMessage: null,
          });
          break;

        default:
          setOfferSubmissionState({
            data: null,
            status: "ERROR",
            errorCode: "UNKNOWN_STATUS",
            errorMessage: "Unexpected response received. Please try again.",
            errorTrace: null,
            loading: false,
            successMessage: null,
          });
      }
    } catch (error) {
      console.error("Error submitting CFD property offer:", error);

      setOfferSubmissionState({
        data: null,
        status: "ERROR",
        errorCode: error.response?.status
          ? `HTTP_${error.response.status}`
          : "NETWORK_ERROR",
        errorMessage:
          error.response?.status === 401
            ? "Please log in to submit an offer."
            : error.response?.status === 403
            ? "You don't have permission to submit this offer."
            : error.response?.status === 409
            ? "This offer conflicts with an existing offer. Please refresh and try again."
            : "An unexpected error occurred while submitting your offer. Please try again later.",
        errorTrace: error.stack || null,
        loading: false,
        successMessage: null,
      });
    } finally {
      // In case of completion (whether success or error), hide the loading state
      setOfferSubmissionState((prevState) => ({
        ...prevState,
        loading: false,
      }));
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      debouncedFetchOfferDetails.cancel();
    };
  }, [debouncedFetchOfferDetails]);

  // Created a function to reset disclaimer modal state
  const resetDisclaimerModalState = () => {
    trackButtonClick({
      eventName: "cfd_view_disclaimer_closed",
      eventCategory: "CFD",
      eventSource: "on modal close",
    });

    setDisclaimerModalState({
      isLoading: false,
      disclaimerData: null,
      error: null,
    });
    setShowDisclaimerModal(false);
  };

  // Created a function to reset offer history modal state
  const resetOfferHistoryModalState = () => {
    trackButtonClick({
      eventName: "cfd_close_view_offer_history_clicked",
      eventCategory: "CFD",
      eventSource: "on offer history modal close",
    });

    setHistoryModalState({
      isLoading: false,
      offerHistoryData: null,
      error: null,
    });
    setShowOfferHistory(false);
  };

  // Created a function to reset make offer modal state
  const resetMakeOfferModalState = () => {
    trackButtonClick({
      eventName: "cfd_close_make_offer_modal",
      eventCategory: "CFD",
      eventSource: "on modal close",
    });

    setMakeOfferModalState({
      isLoading: false,
      propertyData: null,
      error: null,
    });
    setShowPlaceOffer(false);
    setOfferAmount(0);
    setOfferAmountError("");
    setBuyersPremiumData({
      bidAmount: 0,
      bidAmountFormatted: 0,
      buyerPremiumFormatted: 0,
      totalDueFormatted: 0,
      buyerPremium: 0,
      premiumPaidBy: "",
      technologyFee: null,
      earnestMoney: null,
      earnestMoneyMessage: null,
    });
    setOfferSubmissionState({
      data: null,
      status: "",
      errorCode: null,
      errorMessage: null,
      errorTrace: null,
      loading: false,
      successMessage: null,
    });
  };

  // Created a function to reset offer summary modal state
  const resetOfferSummaryModalState = () => {
    trackButtonClick({
      eventName: "cfd_close_offer_summary_model_clicked",
      eventCategory: "CFD",
      eventSource: "on offer summary modal close",
    });

    setOfferSummaryModalState({
      isLoading: false,
      propertyData: null,
      error: null,
    });
    setShowOfferSummaryModal(false);
  };

  // Function to format numbers with commas and dollar sign
  const formatPrice = (price) => {
    if (price) return `$${price.toLocaleString()}`;
    else return price;
  };

  return (
    <div className="cfd-footer-section">
      {/* Favorite Button - Bottom Right */}
      {!isModalOpen && (
        <div className="favorite-button">
          <CfdFavoriteButton
            item={item}
            isPropertySaved={isPropertySaved}
            setIsPropertySaved={setIsPropertySaved}
            setShowModal={setShowModal}
            activeTab={activeTab}
          />
        </div>
      )}
      <div className="cfd-footer-container">
        <div className="cfd-offer-info">
          {parseInt(currentBid) > 0 && (
            <>
              <p className="cfd-offer-label">
                {activeTab === "history"
                  ? "Latest Offer"
                  : "Your Current Offer"}
              </p>
              <p className="cfd-offer-amount">${currentBid}</p>
            </>
          )}
          {item.cfdPropertyDisclaimerFlag === "Y" && (
            <button
              className="cfd-disclaimer-btn"
              onClick={() => {
                setShowDisclaimerModal(true);
                getPropertyDisclaimer();
              }}
            >
              View Disclaimers
            </button>
          )}
          {historyTab && item.offerPlacedOn && (
            <button
              className="cfd-disclaimer-btn"
              onClick={() => {
                setShowOfferHistory(true);
                retrieveOfferHistory();
              }}
            >
              View Offer History
            </button>
          )}
        </div>

        <div className="cfd-action-buttons">
          <button
            type="button"
            className="btn btn-sm cfd-btn-thm"
            aria-label="cfd"
            onClick={onNavigatePDP}
          >
            View Details
          </button>

          {(!isSavedTab || item?.isCfdPropertyOfCurrentCFDCycle === "Y") && (
            <button
              type="button"
              aria-label="history"
              className="btn btn-sm cfd-btn-thm"
              onClick={() => {
                if (activeTab === "history") {
                  setShowOfferSummaryModal(true);
                  retrieveOfferSummary(propertyId, item.cfdOfferId);
                } else {
                  setShowPlaceOffer(true);
                  fetchPropertyDetailsForMakeAnOffer(parseInt(currentBid) > 0);
                }
              }}
            >
              {activeTab === "history"
                ? "Offer Summary"
                : parseInt(currentBid) > 0
                ? "Place New Offer"
                : "Make An Offer"}
            </button>
          )}
        </div>
      </div>

      {/* Bootstrap Modal Disclaimer*/}
      <DisclaimerModal
        showDisclaimerModal={showDisclaimerModal}
        resetDisclaimerModalState={resetDisclaimerModalState}
        disclaimerModalState={disclaimerModalState}
      />

      {/* Bootstrap Modal Offer History*/}
      <OfferHistoryModal
        showOfferHistory={showOfferHistory}
        historyModalState={historyModalState}
        formatPrice={formatPrice}
        resetOfferHistoryModalState={resetOfferHistoryModalState}
      />

      {/*Bootstrap Make Offer Modal */}
      <MakeOfferModal
        item={item}
        minBidAmount={minBidAmount}
        onRefresh={onRefresh}
        activeTab={activeTab}
        showPlaceOffer={showPlaceOffer}
        setShowPlaceOffer={setShowPlaceOffer}
        makeOfferModalState={makeOfferModalState}
        offerAmount={offerAmount}
        offerAmountError={offerAmountError}
        buyersPremiumData={buyersPremiumData}
        formatPrice={formatPrice}
        handleInputChange={handleInputChange}
        handleIncrement={handleIncrement}
        handleDecrement={handleDecrement}
        isLoading={isLoading}
        handleSubmitOffer={handleSubmitOffer}
        offerSubmissionState={offerSubmissionState}
        resetMakeOfferModalState={resetMakeOfferModalState}
      />

      {/*Bootstrap Offer History Summary Modal */}
      <OfferSummaryModal
        item={item}
        activeTab={activeTab}
        showOfferSummaryModal={showOfferSummaryModal}
        offerSummaryModalState={offerSummaryModalState}
        formatPrice={formatPrice}
        resetOfferSummaryModalState={resetOfferSummaryModalState}
      />
    </div>
  );
};

export default CfdFooterSection;
